import { useEffect, useRef, useState } from "react";
import { ADS_PURCHASE_EVENT, purchaseValue, track } from "../lib/analytics";
import { isManualProvider } from "../../shared/payments";
import { ManualPayment } from "./order/ManualPayment";
import { formatArs } from "../../shared/money";
import { Link, Navigate, useParams, useSearchParams } from "react-router";
import { RESERVATION_MINUTES } from "../../shared/payments";
import type { OrderSummary } from "../../shared/types";
import { useAuth } from "../auth/AuthProvider";
import { ErrorMessage, Loading } from "../components/StatusMessage";
import { formatUsd } from "../../shared/money";
import { useI18n } from "../i18n/I18nProvider";
import { useCatalog } from "../lib/CatalogProvider";
import { ApiError, api, formatCharge } from "../lib/api";

const POLL_MS = 3000;
const MAX_POLLS = 20;

export function OrderPage() {
  const { number = "" } = useParams();
  const [params] = useSearchParams();
  const { t, locale } = useI18n();
  // Orders are recorded in USD; what was charged (pesos or dollars) is shown next to the total.
  const formatPrice = (cents: number) => formatUsd(cents, locale);
  const auth = useAuth();
  const [order, setOrder] = useState<OrderSummary | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [polls, setPolls] = useState(0);
  const captureStarted = useRef(false);
  const cancelled = params.get("cancelled") === "1";
  // Access key from the payment return / email link: the page works without a session (on a phone
  // the provider often sends the buyer back to another browser).
  const key = params.get("k");
  const canLoad = Boolean(auth.user || key);

  useEffect(() => {
    if (!canLoad) return;
    const controller = new AbortController();
    const load = async () => {
      try {
        let current = await api.order(number, controller.signal, auth.user ? null : key);
        // Back from PayPal with an approved order: capture it (the webhook is the fallback).
        if (!cancelled && current.paymentProvider === "paypal" && params.get("token") && current.paymentStatus === "unpaid" && !captureStarted.current) {
          captureStarted.current = true;
          current = await api.capturePayPal(number, auth.user ? null : key).catch(() => current);
        }
        if (!controller.signal.aborted) setOrder(current);
      } catch (err) {
        if (!controller.signal.aborted) setError(err instanceof Error ? err : new Error(String(err)));
      }
    };
    void load();
    return () => controller.abort();
  }, [canLoad, auth.user, key, number, polls, cancelled, params]);

  // Analytics: the purchase, once per order and browser, when it shows as paid.
  useEffect(() => {
    if (order?.paymentStatus !== "paid") return;
    const key = `pf-ga-purchase-${order.orderNumber}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, "1");
    } catch {
      // no storage: may be sent again on a reload, GA dedupes by transaction_id
    }
    // Google Ads imports this from GA4 as the "Purchase" conversion: the amount actually charged.
    const charged = purchaseValue(order);
    track("purchase", {
      transaction_id: order.orderNumber,
      ...charged,
      shipping: order.shippingArs ?? 0,
      coupon: order.couponCode ?? undefined,
      // Item prices are in dollars and the event in pesos, so items carry no price (GA allows it).
      items: order.items.map((i) => ({ item_id: i.name, item_name: i.name, quantity: i.quantity })),
    });
    // Google Ads' own "Purchase" conversion (Goals › Conversions), sent through the same Google tag.
    track(ADS_PURCHASE_EVENT, { transaction_id: order.orderNumber, ...charged });
  }, [order]);

  // Poll while the provider confirms (webhooks can lag the browser redirect by a few seconds).
  useEffect(() => {
    if (!order || cancelled || polls >= MAX_POLLS) return;
    if (order.paymentStatus !== "unpaid" && order.paymentStatus !== "pending") return;
    const id = window.setTimeout(() => setPolls((n) => n + 1), POLL_MS);
    return () => window.clearTimeout(id);
  }, [order, polls, cancelled]);

  if (auth.loading) return <Loading />;
  if (!canLoad) return <Navigate to={`/ingresar?next=${encodeURIComponent(`/pedido/${number}`)}`} replace />;
  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="container section">
        <ErrorMessage message={notFound ? t("product.notFound") : undefined} />
      </div>
    );
  }
  if (!order) return <Loading label={t("order.checking")} />;

  const status = order.paymentStatus;
  const tone = status === "paid" ? "ok" : status === "unpaid" || status === "pending" ? "wait" : "bad";
  // Bank transfer / crypto: no gateway to wait for; show how to pay instead.
  const manual = isManualProvider(order.paymentProvider);

  return (
    <div className="container section order-page">
      <h1>{t("order.title", { number: order.orderNumber })}</h1>
      <div className={`order-status order-status-${tone}`} role="status" aria-live="polite">
        <strong>{t(`order.status.${status}`)}</strong>
        <p>{cancelled && tone === "wait" ? t("order.cancelled") : manual && tone === "wait" ? t("order.text.manual") : t(`order.text.${status}`, { minutes: RESERVATION_MINUTES })}</p>
        {tone === "wait" && !cancelled && !manual && polls < MAX_POLLS && (
          <span className="muted small">
            <span className="spinner" aria-hidden="true" /> {t("order.checking")}
          </span>
        )}
      </div>

      {manual && tone === "wait" && !cancelled && <ManualPayment order={order} />}

      <div className="order-details">
        <dl className="product-meta">
          <dt>{t("order.date")}</dt>
          <dd>{new Date(order.createdAt).toLocaleString(locale === "es" ? "es-AR" : "en-US")}</dd>
          {order.paymentProvider && (
            <>
              <dt>{t("checkout.payment")}</dt>
              <dd>{t(`checkout.pay.${order.paymentProvider}`)}</dd>
            </>
          )}
          {order.chargeAmountMinor !== null && (
            <>
              <dt>{t("checkout.total")}</dt>
              <dd>
                {formatPrice(order.total)}
                {order.chargeAmountMinor != null &&
                  (order.chargeCurrency === "ARS" || order.chargeAmountMinor !== order.total) &&
                  ` · ${t("order.charged", { amount: formatCharge(order.chargeAmountMinor, order.chargeCurrency, locale) })}`}
              </dd>
            </>
          )}
        </dl>
        <h2 className="section-title">{t("order.items")}</h2>
        <ul className="order-items">
          {order.items.map((item, i) => (
            <li key={i}>
              <span>
                {item.quantity} × {item.name}
              </span>
              <span>{formatPrice(item.unitPrice * item.quantity)}</span>
            </li>
          ))}
          {order.couponCode && (order.couponMinor ?? 0) > 0 && order.chargeAmountMinor != null && (
            <li>
              <span>{t("order.coupon", { code: order.couponCode })}</span>
              <span>−{formatCharge(order.couponMinor!, order.chargeCurrency, locale)}</span>
            </li>
          )}
          {order.delivery === "shipping" && (
            <li>
              <span>{t("checkout.shippingRow")}</span>
              <span>{order.shippingArs === null ? t("checkout.shippingArrange") : order.shippingArs === 0 ? t("checkout.shippingFree") : formatArs(order.shippingArs, locale)}</span>
            </li>
          )}
        </ul>
      </div>

      {order.delivery === "pickup" && <p className="notice">{t("checkout.pickupNote")}</p>}
      {!auth.user && order.guest && order.customerEmail ? <CreateAccountCard email={order.customerEmail} /> : !auth.user && <p className="muted">{t("order.guestNote")}</p>}

      <div className="btn-row">
        {!(order.guest && !auth.user) && (
          <Link to={auth.user ? "/cuenta" : `/ingresar?next=${encodeURIComponent("/cuenta")}`} className="btn btn-outline">
            {t(auth.user ? "account.orders" : "auth.login")}
          </Link>
        )}
        <Link to="/mercado" className="btn btn-primary">
          {t("order.payAgain")}
        </Link>
      </div>
    </div>
  );
}

/**
 * After buying without an account: create one with the order's email to see this and future
 * orders in Mi cuenta (orders with that email show up once it's verified), save the details for
 * next time and, while the offer is on, get the welcome discount for subscribing.
 */
function CreateAccountCard({ email }: { email: string }) {
  const { t, pick } = useI18n();
  const { settings } = useCatalog();
  const offerOn = settings.newsletter_offer_enabled ? pick(settings.newsletter_offer_enabled) !== "off" : false;
  const pct = Number(settings.newsletter_offer_pct ? pick(settings.newsletter_offer_pct) : 5) || 5;
  const to = `/registro?email=${encodeURIComponent(email)}&next=${encodeURIComponent("/cuenta")}`;
  return (
    <section className="create-account" aria-labelledby="ca-title">
      <h2 id="ca-title">{t("order.accountTitle")}</h2>
      <p>{t("order.accountText", { email })}</p>
      <ul>
        <li>{t("order.accountPerk1")}</li>
        <li>{t("order.accountPerk2")}</li>
        {offerOn && <li>{t("order.accountPerk3", { pct })}</li>}
      </ul>
      <div className="btn-row">
        <Link to={to} className="btn btn-primary">
          {t("order.accountCta")}
        </Link>
        <Link to={`/ingresar?next=${encodeURIComponent("/cuenta")}`} className="btn btn-link">
          {t("order.accountHave")}
        </Link>
      </div>
    </section>
  );
}
